import {
  useCallback,
  useEffect,
} from 'react';
import isNil from 'lodash/isNil';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { PatientPCCItemsDTO } from 'artifacts/dtos';
import useIntakeFlags, { IntakeFlags } from 'hooks/useIntakeFlags';
import { appStateSettingsAction, AppState } from 'reduxStateSlice/appState';
import { ModuleTypes } from 'types/appState';
import { isTabletBaseIntake } from 'env';
import { isEmptyString } from 'utils/helper';

export type { IntakeFlags as TempoFlags };

export default function useIncompleteTempoRedirect(
  flagsOrShouldRedirect?: IntakeFlags | boolean | null,
  pccItemsOrShouldRedirect?: PatientPCCItemsDTO | boolean | null,
  shouldRedirectArg: boolean = false,
) {
  // Parse the overloaded arguments
  let providedFlags: IntakeFlags | null = null;
  let providedPccItems: PatientPCCItemsDTO | null = null;
  let shouldRedirect = false;

  if (typeof flagsOrShouldRedirect === 'boolean') {
    shouldRedirect = flagsOrShouldRedirect;
  } else if (typeof pccItemsOrShouldRedirect === 'boolean') {
    providedFlags = flagsOrShouldRedirect ?? null;
    shouldRedirect = pccItemsOrShouldRedirect;
  } else {
    providedFlags = flagsOrShouldRedirect ?? null;
    providedPccItems = (pccItemsOrShouldRedirect as PatientPCCItemsDTO) ?? null;
    shouldRedirect = shouldRedirectArg;
  }

  const dispatch = useDispatch();
  const { appointmentToken } = useSelector(
    (state: RootState) => state.appStateSettings,
  );
  const isTablet = isTabletBaseIntake || !isNil(appointmentToken);

  const { flags, pccItems, isLoading } = useIntakeFlags(
    !isNil(providedFlags)
      ? { intakeFlags: providedFlags, pccItems: providedPccItems }
      : undefined,
  );

  const isPCC = flags.isPCCEnabledForOrg ?? false;
  const isTempoEnabledForOrg = flags.isTempoEnabledForOrg ?? false;
  const shouldShowTempo = flags.shouldShowTempo ?? false;

  const hasIncompleteTempo = isTempoEnabledForOrg && shouldShowTempo;
  const isTempoComplete = isTempoEnabledForOrg && !shouldShowTempo;

  const goToCommuniqueChoice = useCallback(() => {
    dispatch(appStateSettingsAction({
      name: ModuleTypes.communiqueByPatientInfo,
      page: 'choice',
    }));
  }, [dispatch]);

  const startTempo = useCallback(() => {
    const target: AppState = isPCC && !isEmptyString(pccItems?.tempo?.token)
      ? { name: ModuleTypes.tempo, page: null, tempoToken: pccItems?.tempo?.token ?? '' }
      : { name: ModuleTypes.tempo, page: null };
    dispatch(appStateSettingsAction(target));
  }, [dispatch, isPCC, pccItems?.tempo?.token]);

  useEffect(() => {
    if (shouldRedirect && isTablet) {
      goToCommuniqueChoice();
    }
  }, [goToCommuniqueChoice, isTablet, shouldRedirect]);

  return {
    goToCommuniqueChoice,
    handleRedirectToForm: goToCommuniqueChoice,
    hasIncompleteTempo,
    isLoadingTempo: isLoading,
    isTablet,
    isTempoComplete,
    isTempoEnabledForOrg,
    shouldShowTempo,
    startTempo,
  };
}
